import { AxiosResponse } from "axios";
import {
    RemoteExecutionServiceImpl,
    RemoteGETCallInputObject,
    ServerResponse,
    RemotePOSTCallInputObject,
} from "../../remoteExecution/RemoteExecutionServiceImpl";
import {
    AssignJobberToShiftRequestDTO,
    ShiftRequestAssignmentReqObjWJobber,
    AgencyObjClient,
    CurrentAgencyPayStubsRespDTO,
    CurrentAgencyPayStubsJobberLevelDTO,
    AgencyOrgBillingCycleSummaryDTO,
    TimeClockDataRespDTO,
    DownloadOrgBillReqDTO,
    DownloadOrgBillRespDTO,
    DownloadJobberPayStubAgencyReqDTO,
    DownloadJobberPayStubAgencyResDTO,
    JobberFullProfileDetails,
    DirectShiftAssignmentResp,
    RegisterJobberRequestSimpleDTO,
    CreateJobberWorkerTypeReqDTO,
    CreateOrgToAgencyObjReqDTO,
    HireJobberAgencyReq,
    UpdateJobberWorkerTypePayRateReqDTO,
    ModifyJobberWorkerHourlyRateReqDTO,
    ModifyOrgBillingRateReqDTO,
    ModifyShiftActualTimesReqDTO,
    ModifyShiftClockTimesReqDTO,
    CancelShiftPaymentWorkerPayReqDTO,
    CancelShiftPaymentOrgBillingReqDTO,
    UpdateAgencyOrgBillStatusReqDTO,
    AddFacilityToOrgRequestDTO,
    AddWorkerTypeToOrgRequestDTO,
    UpdateOrgWorkerTypePayRateRequestDTO,
    UpdateDefaultWorkerTypePayRateRequestDTO,
    UpdateJobberWorkerTypeLaborSalesTaxRequestDTO,
    UpdateOrgWorkerTypeLaborSalesTaxRequestDTO,
    UpdateOrgBillingWorkerTypeNameRequestDTO,
    ChangePayCycleStatusReqDTO,
    GetPayrollCycleReqDTO,
    ShiftPaymentDTO,
    CreateCorpJobberEmploymentDeductionReqDTO,
    UpdateJobberNoteOnEmploymentConfig,
    UpdateJobberQuickBooksHoursSyncReqDTO,
    AddJobberWorkerTypeReqDTO,
    JobberAvalTimeWindows,
    RegisterJobberRequestSimpleJobberworkerTypeDTO,
    MapJobbersToOrgLocationRequestDTO,
    UpdateOrgLocationJobberMatchingTypeRequestDTO,
    ChangeJobberStatusReqDTO,
    ResendJobberRegistrationEmailRequestDTO,
    CreateShiftTemplateRequestDTO,
    AddOrgLocationHourlyDeductionConfigRequestDTO,
    DeleteOrgLocationHourlyDeductionConfigRequestDTO,
    RemoveWorkerTypeFromOrgRequestDTO,
    UpdateOrgDisplayNameRequestDTO,
    ShiftAssignmentConfigRuleType,
    ShiftAssignmentConfigRuleApplicationType,
    ListAgencyOrgBillingCycleSummaryHeadersRespDTO,
    SearchAgencyOrgBillsByInvoiceNumberRespDTO,
} from "../agency-data/AgencyInterfaces";
import type {
    CorpToOrgClientBillingConfigDTO,
    CreateCorpToOrgClientBillingConfigDTO,
} from "../../orgViews/orgConstants";
import type { ShiftPaymentWReceiptLinesAgencyRespDTO } from "../agency-data/ShiftPaymentCatalogReceiptAgencyInterfaces";
import {
    CreateAgencyInvoiceDisplayConfigTemplateRequest,
    AgencyInvoiceDisplayConfigTemplateResponse,
} from "./AgencyInvoiceDisplayConfigInterfaces";
import { UpdateAgencyOrgInvoiceConfigSpecRequestDTO, AgencyOrgInvoiceConfigSpecDTO } from "../../common/CommonInterfaces";

interface CreateShiftAssignmentConfigRuleDetail {
    shiftAssignmentConfigRuleType: ShiftAssignmentConfigRuleType;
    shiftAssignmentConfigRuleApplicationType?: ShiftAssignmentConfigRuleApplicationType;
}

const backendApiBasePathLocal = process.env.REACT_APP_BACK_END_SERVER_BASE_PATH_LOCAL;
const remoteCallObject = RemoteExecutionServiceImpl();

const AssignJobberToShiftAPICall = async (assignRequest: AssignJobberToShiftRequestDTO): Promise<DirectShiftAssignmentResp | null> => {
    const postCallInput: RemotePOSTCallInputObject<AssignJobberToShiftRequestDTO> = {
        apiUrl: `/shiftrequest/assign-jobber-to-shift`, 
        headers: {},
        object: assignRequest,
    };

    try {
        const apiResponse: AxiosResponse<ServerResponse<DirectShiftAssignmentResp, Error>> =
            await remoteCallObject.remotePOSTCallV2<
                ServerResponse<DirectShiftAssignmentResp, Error>,
                AssignJobberToShiftRequestDTO
            >(postCallInput);

        if (apiResponse && (apiResponse.status === 200 || apiResponse.status === 201)) {
            if (apiResponse.data && apiResponse.data.data) {
                return apiResponse.data.data;
            }
            return null;
        }
        return null;
    } catch (e) {
        console.error("Error assigning jobber to shift", e);
        throw e;
    }
};

const CreateShiftAssignmentConfigRuleAPICall = async (createRuleReq: CreateShiftAssignmentConfigRuleDetail): Promise<AgencyObjClient | null> => {
    const postCallInput: RemotePOSTCallInputObject<CreateShiftAssignmentConfigRuleDetail> = {
        apiUrl: `${backendApiBasePathLocal}agency/actions/create-shift-assignment-config-rule`,
        headers: {},
        object: createRuleReq,
    };

    const apiResponse: AxiosResponse<ServerResponse<AgencyObjClient, Error>> =
        await remoteCallObject.remotePOSTCallV2<
            ServerResponse<AgencyObjClient, Error>,
            CreateShiftAssignmentConfigRuleDetail
        >(postCallInput);

    if (apiResponse && apiResponse.data && apiResponse.data.data) {
        return apiResponse.data.data;
    } else {
        return null;
    }
};

const ChangeJobberStatusAPICall = async (changeStatusReq: ChangeJobberStatusReqDTO): Promise<string | null> => {
  const postCallInput: RemotePOSTCallInputObject<ChangeJobberStatusReqDTO> = {
    apiUrl: `${backendApiBasePathLocal}agency/jobber/status-change/`,
    headers: {},
    object: changeStatusReq,
  };

  const apiResponse: AxiosResponse<ServerResponse<string, Error>> =
    await remoteCallObject.remotePOSTCallV2<
      ServerResponse<string, Error>,
      ChangeJobberStatusReqDTO
    >(postCallInput);

  if (apiResponse && apiResponse.data && apiResponse.data.data) {
    return apiResponse.data.data;
  } else {
    return null;
  }
};

const GetAgencyWTokenOnly = async ({}: {}) => {
  const getCallInput: RemoteGETCallInputObject = {
    apiUrl: backendApiBasePathLocal + "agency/",
    headers: {},
    queryParams: null,
  };
  const agencyResponse: AxiosResponse<ServerResponse<AgencyObjClient, Error>> = await remoteCallObject.remoteGETCallV2<
    ServerResponse<AgencyObjClient, Error>
  >(getCallInput);

  if (agencyResponse && agencyResponse.data && agencyResponse.data.data) {
    return agencyResponse.data.data;
  } else {
    return null;
  }
};


const GetAllPaystubAPICall = async (): Promise<CurrentAgencyPayStubsRespDTO[] | null> => {
  const getCallInput: RemoteGETCallInputObject = {
    apiUrl: `${backendApiBasePathLocal}workypay/agency/all/`,
    headers: { },
    queryParams: null,
  };

  const paystubResponse: AxiosResponse<ServerResponse<CurrentAgencyPayStubsRespDTO[], Error>> = await remoteCallObject.remoteGETCallV2<
    ServerResponse<CurrentAgencyPayStubsRespDTO[], Error>
  >(getCallInput);

  if (paystubResponse && paystubResponse.data && paystubResponse.data.data) {
    return paystubResponse.data.data;
  } else {
    return null;
  }
};


const GetOrgBillsAPICall = async (): Promise<AgencyOrgBillingCycleSummaryDTO[] | null> => {
  const getCallInput: RemoteGETCallInputObject = {
    apiUrl: `${backendApiBasePathLocal}agency/org-bill/`,
    headers: { },
    queryParams: null,
  };

  const orgBillsResponse: AxiosResponse<ServerResponse<AgencyOrgBillingCycleSummaryDTO[], Error>> = await remoteCallObject.remoteGETCallV2<
    ServerResponse<AgencyOrgBillingCycleSummaryDTO[], Error>
  >(getCallInput);

  if (orgBillsResponse && orgBillsResponse.data && orgBillsResponse.data.data) {
    return orgBillsResponse.data.data;
  } else {
    return null;
  }
};

const GetOrgBillHeadersPageAPICall = async (
  pageSize: number,
  pageToken: string | null,
  orgName: string | null = null
): Promise<ListAgencyOrgBillingCycleSummaryHeadersRespDTO | null> => {
  const getCallInput: RemoteGETCallInputObject = {
    apiUrl: `${backendApiBasePathLocal}agency/org-invoices/summary-list/`,
    headers: {},
    queryParams: {
      pageSize: pageSize.toString(),
      ...(pageToken ? { pageToken: pageToken } : {}),
      ...(orgName ? { orgName: orgName } : {}),
    },
  };

  const resp: AxiosResponse<ServerResponse<ListAgencyOrgBillingCycleSummaryHeadersRespDTO, Error>> =
    await remoteCallObject.remoteGETCallV2<
      ServerResponse<ListAgencyOrgBillingCycleSummaryHeadersRespDTO, Error>
    >(getCallInput);

  if (resp && resp.data && resp.data.data) {
    return resp.data.data;
  } else {
    return null;
  }
};

const SearchOrgBillsByInvoiceNumberAPICall = async (
  invoiceNumberPrefix: string
): Promise<SearchAgencyOrgBillsByInvoiceNumberRespDTO | null> => {
  const getCallInput: RemoteGETCallInputObject = {
    apiUrl: `${backendApiBasePathLocal}agency/org-invoices/search-by-invoice-number/`,
    headers: {},
    queryParams: {
      invoiceNumberPrefix: invoiceNumberPrefix,
    },
  };

  const resp: AxiosResponse<ServerResponse<SearchAgencyOrgBillsByInvoiceNumberRespDTO, Error>> =
    await remoteCallObject.remoteGETCallV2<
      ServerResponse<SearchAgencyOrgBillsByInvoiceNumberRespDTO, Error>
    >(getCallInput);

  if (resp && resp.data && resp.data.data) {
    return resp.data.data;
  } else {
    return null;
  }
};

const GetOrgBillingCycleSummaryAPICall = async (
  orgName: string, 
  billingCycleStartEpoch: number, 
  billingCycleEndEpoch: number
): Promise<AgencyOrgBillingCycleSummaryDTO | null> => {
  const getCallInput: RemoteGETCallInputObject = {
    apiUrl: `${backendApiBasePathLocal}agency/org-billing/org-billing-cycle-summary`,
    headers: {},
    queryParams: {
      orgName: orgName,
      billingCycleStartEpoch: billingCycleStartEpoch.toString(),
      billingCycleEndEpoch: billingCycleEndEpoch.toString()
    },
  };

  try {
    const response: AxiosResponse<ServerResponse<AgencyOrgBillingCycleSummaryDTO, Error>> = await remoteCallObject.remoteGETCallV2<
      ServerResponse<AgencyOrgBillingCycleSummaryDTO, Error>
    >(getCallInput);

    if (response && response.data && response.data.data) {
      return response.data.data;
    }
    return null;
  } catch (error) {
    console.error("Error fetching org billing cycle summary:", error);
    return null;
  }
};

const CreateCorpToOrgClientBillingConfigAPICall = async (createCorpToOrgClientBillingConfigDTO: CreateCorpToOrgClientBillingConfigDTO): Promise<boolean> => {
  const postCallInput: RemotePOSTCallInputObject<CreateCorpToOrgClientBillingConfigDTO> = {
    apiUrl: `${backendApiBasePathLocal}agency/org-billing/create-corp-to-org-client-billing-config`,
    headers: {},
    object: createCorpToOrgClientBillingConfigDTO,
  };

  try {
    const response: AxiosResponse<ServerResponse<void, Error>> = await remoteCallObject.remotePOSTCallV2<
      ServerResponse<void, Error>,
      CreateCorpToOrgClientBillingConfigDTO
    >(postCallInput);

    if (response && response.data) {
      return true;
    } else {
      return false;
    }
  } catch (error) {
    console.error("Error creating corp to org client billing config:", error);
    return false;
  }
};

const UploadBillingConfigIconAPICall = async (
  iconFile: File,
  corpToOrgClientBillingConfigId: string
): Promise<CorpToOrgClientBillingConfigDTO | null> => {
  const formDataReq = new FormData();
  formDataReq.append('iconFile', iconFile, iconFile.name);
  formDataReq.append('corpToOrgClientBillingConfigId', corpToOrgClientBillingConfigId);

  const postCallInput: RemotePOSTCallInputObject<FormData> = {
    apiUrl: `${backendApiBasePathLocal}v1/agency/billing-config/billing-pdf-upload-icon`,
    object: formDataReq,
    headers: { 'Content-Type': 'multipart/form-data' },
  };

  try {
    const response: AxiosResponse<ServerResponse<CorpToOrgClientBillingConfigDTO, Error>> = 
      await remoteCallObject.remotePOSTCallV2<
        ServerResponse<CorpToOrgClientBillingConfigDTO, Error>,
        FormData
      >(postCallInput);

    if (response && response.data && response.data.data) {
      return response.data.data;
    }
    return null;
  } catch (error) {
    console.error("Error uploading billing config icon:", error);
    throw error;
  }
};


const GetActiveSATimeClock = async (): Promise<TimeClockDataRespDTO | null> => {
  const getCallInput: RemoteGETCallInputObject = {
    apiUrl: `${backendApiBasePathLocal}timeclock/agency/get-active-shifts`,
    headers: { },
    queryParams: null,
  };

  const orgBillsResponse: AxiosResponse<ServerResponse<TimeClockDataRespDTO, Error>> = await remoteCallObject.remoteGETCallV2<
    ServerResponse<TimeClockDataRespDTO, Error>
  >(getCallInput);

  if (orgBillsResponse && orgBillsResponse.data && orgBillsResponse.data.data) {
    return orgBillsResponse.data.data;
  } else {
    return null;
  }
};


const PostRetrieveOrgAgencyBillPDF = async (downloadPDfReq: DownloadOrgBillReqDTO): Promise<DownloadOrgBillRespDTO | null> => {
  const postCallInput: RemotePOSTCallInputObject<DownloadOrgBillReqDTO> = {
    apiUrl: `${backendApiBasePathLocal}agency/org-billing/download-org-bill-pdf`,
    headers: { },
    object:downloadPDfReq,    
  };

  const orgBillsResponse: AxiosResponse<ServerResponse<DownloadOrgBillRespDTO, Error>> = 
  await remoteCallObject.remotePOSTCallV2<ServerResponse<DownloadOrgBillRespDTO, Error>,DownloadOrgBillReqDTO>(postCallInput);

  if (orgBillsResponse && orgBillsResponse.data && orgBillsResponse.data.data) {
    return orgBillsResponse.data.data;
  } else {
    return null;
  }

};

/**
 * Download the agency org bill CSV for the given org and billing cycle (primary invoice).
 * Returns a presigned download URL if CSV_ALREADY_EXISTS; otherwise UNABLE_TO_GENERATE_CSV.
 */
const PostDownloadOrgBillCsv = async (downloadReq: DownloadOrgBillReqDTO): Promise<DownloadOrgBillRespDTO | null> => {
  const postCallInput: RemotePOSTCallInputObject<DownloadOrgBillReqDTO> = {
    apiUrl: `${backendApiBasePathLocal}agency/org-billing/download-org-bill-csv`,
    headers: {},
    object: downloadReq,
  };

  const response: AxiosResponse<ServerResponse<DownloadOrgBillRespDTO, Error>> =
    await remoteCallObject.remotePOSTCallV2<ServerResponse<DownloadOrgBillRespDTO, Error>, DownloadOrgBillReqDTO>(postCallInput);

  if (response?.data?.data) {
    return response.data.data;
  }
  return null;
};

const PostRetrieveJobberPayStubBillPDF = async (downloadPDfReq: DownloadJobberPayStubAgencyReqDTO): Promise<DownloadJobberPayStubAgencyResDTO | null> => {
  const postCallInput: RemotePOSTCallInputObject<DownloadJobberPayStubAgencyReqDTO> = {
    apiUrl: `${backendApiBasePathLocal}workypay/agency/download-jobber-paystub-pdf`,
    headers: { },
    object:downloadPDfReq,    
  };

  const orgBillsResponse: AxiosResponse<ServerResponse<DownloadJobberPayStubAgencyResDTO, Error>> = 
  await remoteCallObject.remotePOSTCallV2<ServerResponse<DownloadJobberPayStubAgencyResDTO, Error>,DownloadJobberPayStubAgencyReqDTO>(postCallInput);

  if (orgBillsResponse && orgBillsResponse.data && orgBillsResponse.data.data) {
    return orgBillsResponse.data.data;
  } else {
    return null;
  }

};



const GetAllJobberAvailbilities = async (): Promise<JobberFullProfileDetails[] | null> => {
  const getCallInput: RemoteGETCallInputObject = {
    apiUrl: `${backendApiBasePathLocal}agency/jobber/full-profile/details`,
    headers: { },
    queryParams: null,
  };

  const orgBillsResponse: AxiosResponse<ServerResponse<JobberFullProfileDetails[], Error>> = await remoteCallObject.remoteGETCallV2<
    ServerResponse<JobberFullProfileDetails[], Error>
  >(getCallInput);

  if (orgBillsResponse && orgBillsResponse.data && orgBillsResponse.data.data) {
    return orgBillsResponse.data.data;
  } else {
    return null;
  }
};

const UpdateJobberNoteOnEmploymentConfigAPICall = async (
  updateReq: UpdateJobberNoteOnEmploymentConfig
): Promise<JobberFullProfileDetails | null> => {
  const postCallInput: RemotePOSTCallInputObject<UpdateJobberNoteOnEmploymentConfig> = {
    apiUrl: `${backendApiBasePathLocal}agency/jobber/actions/jobber-employment-config-note/update`,
    headers: { },
    object: updateReq,
  };

  const apiResponse: AxiosResponse<ServerResponse<JobberFullProfileDetails, Error>> =
    await remoteCallObject.remotePOSTCallV2<
      ServerResponse<JobberFullProfileDetails, Error>,
      UpdateJobberNoteOnEmploymentConfig
    >(postCallInput);

  if (apiResponse && apiResponse.data && apiResponse.data.data) {
    return apiResponse.data.data;
  }

  return null;
};

const UpdateJobberQuickBooksHoursSyncAPICall = async (
  updateReq: UpdateJobberQuickBooksHoursSyncReqDTO
): Promise<JobberFullProfileDetails | null> => {
  const postCallInput: RemotePOSTCallInputObject<UpdateJobberQuickBooksHoursSyncReqDTO> = {
    apiUrl: `${backendApiBasePathLocal}agency/jobber/actions/quickbooks-hours-sync/update`,
    headers: { },
    object: updateReq,
  };

  const apiResponse: AxiosResponse<ServerResponse<JobberFullProfileDetails, Error>> =
    await remoteCallObject.remotePOSTCallV2<
      ServerResponse<JobberFullProfileDetails, Error>,
      UpdateJobberQuickBooksHoursSyncReqDTO
    >(postCallInput);

  if (apiResponse && apiResponse.data && apiResponse.data.data) {
    return apiResponse.data.data;
  }

  return null;
};


const DirectReAssignShiftAPIReq = async (reAssignRequest: ShiftRequestAssignmentReqObjWJobber): Promise<DirectShiftAssignmentResp | null> => {
  
  try{        
    const postCallInput: RemotePOSTCallInputObject<ShiftRequestAssignmentReqObjWJobber> = {
      apiUrl: `${backendApiBasePathLocal}shiftrequest/post-shift-creation/reassign-to-jobber`,
      headers: { },
      object:reAssignRequest,    
    };
  
    const orgBillsResponse: AxiosResponse<ServerResponse<DirectShiftAssignmentResp, Error>> = 
    await remoteCallObject.remotePOSTCallV2<ServerResponse<DirectShiftAssignmentResp, Error>,ShiftRequestAssignmentReqObjWJobber>(postCallInput);
  
    if (orgBillsResponse && orgBillsResponse.data && orgBillsResponse.data.data) {
      return orgBillsResponse.data.data;
    } else {
      return null;
    }
  }catch(e){
    console.log("Exception during call to DirectReAssignShiftAPIReq ", reAssignRequest, e)
    throw e
  }

  

};




const CreateNewJobberWorkerTypePOSTAPICall = async (createNewJobberWorkerTypeForAgency: CreateJobberWorkerTypeReqDTO): Promise<AgencyObjClient | null> => {
  const postCallInput: RemotePOSTCallInputObject<CreateJobberWorkerTypeReqDTO> = {
    apiUrl: `${backendApiBasePathLocal}agency/actions/create-jobber-worker-type`,
    headers: { },
    object:createNewJobberWorkerTypeForAgency,    
  };

  const createWorkerTypeResponse: AxiosResponse<ServerResponse<AgencyObjClient, Error>> = 
  await remoteCallObject.remotePOSTCallV2<ServerResponse<AgencyObjClient, Error>,CreateJobberWorkerTypeReqDTO>(postCallInput);

  if (createWorkerTypeResponse && createWorkerTypeResponse.data && createWorkerTypeResponse.data.data) {
    return createWorkerTypeResponse.data.data;
  } else {
    return null;
  }

};


const RegisterBulkJobbersPOSTAPICall = async (createNewJobberWorkerTypeForAgency: RegisterJobberRequestSimpleDTO[]): Promise<AgencyObjClient | null> => {
  const postCallInput: RemotePOSTCallInputObject<RegisterJobberRequestSimpleDTO[]> = {
    apiUrl: `${backendApiBasePathLocal}agency/actions/register-new-jobber`,
    headers: { },
    object:createNewJobberWorkerTypeForAgency,    
  };

  const createWorkerTypeResponse: AxiosResponse<ServerResponse<AgencyObjClient, Error>> = 
  await remoteCallObject.remotePOSTCallV2<ServerResponse<AgencyObjClient, Error>,RegisterJobberRequestSimpleDTO[]>(postCallInput);

  if (createWorkerTypeResponse && createWorkerTypeResponse.data && createWorkerTypeResponse.data.data) {
    return createWorkerTypeResponse.data.data;
  } else {
    return null;
  }

};



const CreateOrgOnAgency = async (downloadPDfReq: CreateOrgToAgencyObjReqDTO): Promise<AgencyObjClient | null> => {
  const postCallInput: RemotePOSTCallInputObject<CreateOrgToAgencyObjReqDTO> = {
    apiUrl: `${backendApiBasePathLocal}agency/actions/onboard-org-to-agency`,
    headers: { },
    object:downloadPDfReq,    
  };

  const orgBillsResponse: AxiosResponse<ServerResponse<AgencyObjClient, Error>> = 
  await remoteCallObject.remotePOSTCallV2<ServerResponse<AgencyObjClient, Error>,CreateOrgToAgencyObjReqDTO>(postCallInput);

  if (orgBillsResponse && orgBillsResponse.data && orgBillsResponse.data.data) {
    return orgBillsResponse.data.data;
  } else {
    return null;
  }

};

const AddFacilityToOrg = async (addFacilityReq: AddFacilityToOrgRequestDTO): Promise<AgencyObjClient | null> => {
  const postCallInput: RemotePOSTCallInputObject<AddFacilityToOrgRequestDTO> = {
    apiUrl: `${backendApiBasePathLocal}agency/actions/add-facility-to-org`,
    headers: { },
    object: addFacilityReq,    
  };

  const apiResponse: AxiosResponse<ServerResponse<AgencyObjClient, Error>> = 
  await remoteCallObject.remotePOSTCallV2<ServerResponse<AgencyObjClient, Error>, AddFacilityToOrgRequestDTO>(postCallInput);

  if (apiResponse && apiResponse.data && apiResponse.data.data) {
    return apiResponse.data.data;
  } else {
    return null;
  }
};

const AddWorkerTypeToOrg = async (addWorkerTypeReq: AddWorkerTypeToOrgRequestDTO): Promise<AgencyObjClient | null> => {
  const postCallInput: RemotePOSTCallInputObject<AddWorkerTypeToOrgRequestDTO> = {
    apiUrl: `${backendApiBasePathLocal}agency/actions/add-worker-type-to-org`,
    headers: { },
    object: addWorkerTypeReq,    
  };

  const apiResponse: AxiosResponse<ServerResponse<AgencyObjClient, Error>> = 
  await remoteCallObject.remotePOSTCallV2<ServerResponse<AgencyObjClient, Error>, AddWorkerTypeToOrgRequestDTO>(postCallInput);

  if (apiResponse && apiResponse.data && apiResponse.data.data) {
    return apiResponse.data.data;
  } else {
    return null;
  }
};

const UpdateOrgWorkerTypePayRate = async (updatePayRateReq: UpdateOrgWorkerTypePayRateRequestDTO): Promise<AgencyObjClient | null> => {
  const postCallInput: RemotePOSTCallInputObject<UpdateOrgWorkerTypePayRateRequestDTO> = {
    apiUrl: `${backendApiBasePathLocal}agency/actions/update-org-worker-type-pay-rate`,
    headers: { },
    object: updatePayRateReq,    
  };

  const apiResponse: AxiosResponse<ServerResponse<AgencyObjClient, Error>> = 
  await remoteCallObject.remotePOSTCallV2<ServerResponse<AgencyObjClient, Error>, UpdateOrgWorkerTypePayRateRequestDTO>(postCallInput);

  if (apiResponse && apiResponse.data && apiResponse.data.data) {
    return apiResponse.data.data;
  } else {
    return null;
  }
};

const UpdateDefaultWorkerTypePayRate = async (updatePayRateReq: UpdateDefaultWorkerTypePayRateRequestDTO): Promise<AgencyObjClient | null> => {
  const postCallInput: RemotePOSTCallInputObject<UpdateDefaultWorkerTypePayRateRequestDTO> = {
    apiUrl: `${backendApiBasePathLocal}agency/actions/update-default-worker-type-pay-rate`,
    headers: { },
    object: updatePayRateReq,    
  };

  const apiResponse: AxiosResponse<ServerResponse<AgencyObjClient, Error>> = 
  await remoteCallObject.remotePOSTCallV2<ServerResponse<AgencyObjClient, Error>, UpdateDefaultWorkerTypePayRateRequestDTO>(postCallInput);

  if (apiResponse && apiResponse.data && apiResponse.data.data) {
    return apiResponse.data.data;
  } else {
    return null;
  }
};

const UpdateJobberWorkerTypeLaborSalesTax = async (
  request: UpdateJobberWorkerTypeLaborSalesTaxRequestDTO
): Promise<AgencyObjClient | null> => {
  const postCallInput: RemotePOSTCallInputObject<UpdateJobberWorkerTypeLaborSalesTaxRequestDTO> = {
    apiUrl: `${backendApiBasePathLocal}agency/actions/update-jobber-worker-type-labor-sales-tax`,
    headers: {},
    object: request,
  };

  const apiResponse: AxiosResponse<ServerResponse<AgencyObjClient, Error>> =
    await remoteCallObject.remotePOSTCallV2<
      ServerResponse<AgencyObjClient, Error>,
      UpdateJobberWorkerTypeLaborSalesTaxRequestDTO
    >(postCallInput);

  if (apiResponse && apiResponse.data && apiResponse.data.data) {
    return apiResponse.data.data;
  }
  return null;
};

const UpdateOrgWorkerTypeLaborSalesTax = async (
  request: UpdateOrgWorkerTypeLaborSalesTaxRequestDTO
): Promise<AgencyObjClient | null> => {
  const postCallInput: RemotePOSTCallInputObject<UpdateOrgWorkerTypeLaborSalesTaxRequestDTO> = {
    apiUrl: `${backendApiBasePathLocal}agency/actions/update-org-worker-type-labor-sales-tax`,
    headers: {},
    object: request,
  };

  const apiResponse: AxiosResponse<ServerResponse<AgencyObjClient, Error>> =
    await remoteCallObject.remotePOSTCallV2<
      ServerResponse<AgencyObjClient, Error>,
      UpdateOrgWorkerTypeLaborSalesTaxRequestDTO
    >(postCallInput);

  if (apiResponse && apiResponse.data && apiResponse.data.data) {
    return apiResponse.data.data;
  }
  return null;
};

/**
 * Updates the org billing worker type name for a worker type in the agency.
 * Takes workerTypeUniqueIdentifier and the new orgBillingWorkerTypeName; updates the agency and returns the updated agency client.
 */
const UpdateOrgBillingWorkerTypeName = async (
  request: UpdateOrgBillingWorkerTypeNameRequestDTO
): Promise<AgencyObjClient | null> => {
  const postCallInput: RemotePOSTCallInputObject<UpdateOrgBillingWorkerTypeNameRequestDTO> = {
    apiUrl: `${backendApiBasePathLocal}agency/actions/update-org-billing-worker-type-name`,
    headers: {},
    object: request,
  };

  const apiResponse: AxiosResponse<ServerResponse<AgencyObjClient, Error>> =
    await remoteCallObject.remotePOSTCallV2<
      ServerResponse<AgencyObjClient, Error>,
      UpdateOrgBillingWorkerTypeNameRequestDTO
    >(postCallInput);

  if (apiResponse && apiResponse.data && apiResponse.data.data) {
    return apiResponse.data.data;
  }
  return null;
};

const FullyHireJobberAPICall = async (apiRequestBody: HireJobberAgencyReq): Promise<JobberFullProfileDetails | null> => {
  const postCallInput: RemotePOSTCallInputObject<HireJobberAgencyReq> = {
    apiUrl: `${backendApiBasePathLocal}agency/jobber/actions/hire`,
    headers: { },
    object:apiRequestBody,    
  };

  const apiResponse: AxiosResponse<ServerResponse<JobberFullProfileDetails, Error>> = 
  await remoteCallObject.remotePOSTCallV2<ServerResponse<JobberFullProfileDetails, Error>,HireJobberAgencyReq>(postCallInput);

  if (apiResponse && apiResponse.data && apiResponse.data.data) {
    return apiResponse.data.data;
  } else {
    return null;
  }

};





const AddJobberWorkerTypeToJobber = async (apiRequestBody: AddJobberWorkerTypeReqDTO): Promise<JobberFullProfileDetails | null> => {
  const postCallInput: RemotePOSTCallInputObject<AddJobberWorkerTypeReqDTO> = {
    apiUrl: `${backendApiBasePathLocal}agency/jobber/actions/jobber-worker-type/add`,
    headers: { },
    object:apiRequestBody,    
  };

  const apiResponse: AxiosResponse<ServerResponse<JobberFullProfileDetails, Error>> = 
  await remoteCallObject.remotePOSTCallV2<ServerResponse<JobberFullProfileDetails, Error>,AddJobberWorkerTypeReqDTO>(postCallInput);

  if (apiResponse && apiResponse.data && apiResponse.data.data) {
    return apiResponse.data.data;
  } else {
    return null;
  }

};


const UpdateJobberWorkerTypePayRateAPICall = async (apiRequestBody: UpdateJobberWorkerTypePayRateReqDTO): Promise<JobberFullProfileDetails | null> => {
  const postCallInput: RemotePOSTCallInputObject<UpdateJobberWorkerTypePayRateReqDTO> = {
    apiUrl: `${backendApiBasePathLocal}agency/jobber/actions/jobber-worker-type/update-pay-rate`,
    headers: { },
    object:apiRequestBody,    
  };

  const apiResponse: AxiosResponse<ServerResponse<JobberFullProfileDetails, Error>> = 
  await remoteCallObject.remotePOSTCallV2<ServerResponse<JobberFullProfileDetails, Error>,UpdateJobberWorkerTypePayRateReqDTO>(postCallInput);

  if (apiResponse && apiResponse.data && apiResponse.data.data) {
    return apiResponse.data.data;
  } else {
    return null;
  }

};


const ChangePayCycleStatusAPICall = async (changePayCycleStatusReq: ChangePayCycleStatusReqDTO): Promise<CurrentAgencyPayStubsRespDTO | null> => {
  const postCallInput: RemotePOSTCallInputObject<ChangePayCycleStatusReqDTO> = {
    apiUrl: `${backendApiBasePathLocal}workypay/agency/change-pay-cycle-status`,
    headers: { },
    object: changePayCycleStatusReq,    
  };

  const apiResponse: AxiosResponse<ServerResponse<CurrentAgencyPayStubsRespDTO, Error>> = 
  await remoteCallObject.remotePOSTCallV2<ServerResponse<CurrentAgencyPayStubsRespDTO, Error>, ChangePayCycleStatusReqDTO>(postCallInput);

  if (apiResponse && apiResponse.data && apiResponse.data.data) {
    return apiResponse.data.data;
  } else {
    return null;
  }
};

const GetPayrollCycleByDateRangeAPICall = async (getPayrollCycleReq: GetPayrollCycleReqDTO): Promise<CurrentAgencyPayStubsRespDTO | null> => {
  const postCallInput: RemotePOSTCallInputObject<GetPayrollCycleReqDTO> = {
    apiUrl: `${backendApiBasePathLocal}workypay/agency/payroll-cycle`,
    headers: { },
    object: getPayrollCycleReq,    
  };

  const apiResponse: AxiosResponse<ServerResponse<CurrentAgencyPayStubsRespDTO, Error>> = 
  await remoteCallObject.remotePOSTCallV2<ServerResponse<CurrentAgencyPayStubsRespDTO, Error>, GetPayrollCycleReqDTO>(postCallInput);

  if (apiResponse && apiResponse.data && apiResponse.data.data) {
    return apiResponse.data.data;
  } else {
    return null;
  }
};

const GetJobberPayStubForAgencyPayCycleAPICall = async (getJobberPayStubReq: DownloadJobberPayStubAgencyReqDTO): Promise<CurrentAgencyPayStubsJobberLevelDTO | null> => {
  const postCallInput: RemotePOSTCallInputObject<DownloadJobberPayStubAgencyReqDTO> = {
    apiUrl: `${backendApiBasePathLocal}workypay/agency/jobber-paystub`,
    headers: { },
    object: getJobberPayStubReq,    
  };

  const apiResponse: AxiosResponse<ServerResponse<CurrentAgencyPayStubsJobberLevelDTO, Error>> = 
  await remoteCallObject.remotePOSTCallV2<ServerResponse<CurrentAgencyPayStubsJobberLevelDTO, Error>, DownloadJobberPayStubAgencyReqDTO>(postCallInput);

  if (apiResponse && apiResponse.data && apiResponse.data.data) {
    return apiResponse.data.data;
  } else {
    return null;
  }
};

const GetShiftPaymentByShiftIdAPICall = async (shiftId: string): Promise<ShiftPaymentWReceiptLinesAgencyRespDTO | null> => {
  const getCallInput: RemoteGETCallInputObject = {
    apiUrl: `${backendApiBasePathLocal}workypay/agency/shift-payment`,
    headers: { },
    queryParams: { shiftId }
  };

  const apiResponse: AxiosResponse<ServerResponse<ShiftPaymentWReceiptLinesAgencyRespDTO, Error>> = 
  await remoteCallObject.remoteGETCallV2<ServerResponse<ShiftPaymentWReceiptLinesAgencyRespDTO, Error>>(getCallInput);

  if (apiResponse && apiResponse.data && apiResponse.data.data) {
    return apiResponse.data.data;
  } else {
    return null;
  }
};

const ModifyJobberWorkerHourlyRateAPICall = async (modifyReq: ModifyJobberWorkerHourlyRateReqDTO): Promise<ShiftPaymentDTO | null> => {
  const postCallInput: RemotePOSTCallInputObject<ModifyJobberWorkerHourlyRateReqDTO> = {
    apiUrl: `${backendApiBasePathLocal}workypay/agency/modify-jobber-worker-hourly-rate`,
    headers: { },
    object: modifyReq
  };

  const apiResponse: AxiosResponse<ServerResponse<ShiftPaymentDTO, Error>> = 
  await remoteCallObject.remotePOSTCallV2<ServerResponse<ShiftPaymentDTO, Error>, ModifyJobberWorkerHourlyRateReqDTO>(postCallInput);

  if (apiResponse && apiResponse.data && apiResponse.data.data) {
    return apiResponse.data.data;
  } else {
    return null;
  }
};

const ModifyOrgBillingRateAPICall = async (modifyReq: ModifyOrgBillingRateReqDTO): Promise<ShiftPaymentDTO | null> => {
  const postCallInput: RemotePOSTCallInputObject<ModifyOrgBillingRateReqDTO> = {
    apiUrl: `${backendApiBasePathLocal}workypay/agency/modify-org-billing-rate`,
    headers: { },
    object: modifyReq
  };

  const apiResponse: AxiosResponse<ServerResponse<ShiftPaymentDTO, Error>> = 
  await remoteCallObject.remotePOSTCallV2<ServerResponse<ShiftPaymentDTO, Error>, ModifyOrgBillingRateReqDTO>(postCallInput);

  if (apiResponse && apiResponse.data && apiResponse.data.data) {
    return apiResponse.data.data;
  } else {
    return null;
  }
};


const CancelShiftPaymentWorkerPayAPICall = async (req: CancelShiftPaymentWorkerPayReqDTO): Promise<ShiftPaymentDTO | null> => {
  const postCallInput: RemotePOSTCallInputObject<CancelShiftPaymentWorkerPayReqDTO> = {
    apiUrl: `${backendApiBasePathLocal}workypay/agency/cancel-shift-payment-worker-pay`,
    headers: { },
    object: req
  };

  const apiResponse: AxiosResponse<ServerResponse<ShiftPaymentDTO, Error>> =
  await remoteCallObject.remotePOSTCallV2<ServerResponse<ShiftPaymentDTO, Error>, CancelShiftPaymentWorkerPayReqDTO>(postCallInput);

  if (apiResponse && apiResponse.data && apiResponse.data.data) {
    return apiResponse.data.data;
  } else {
    return null;
  }
};

const CancelShiftPaymentOrgBillingAPICall = async (req: CancelShiftPaymentOrgBillingReqDTO): Promise<boolean> => {
  const postCallInput: RemotePOSTCallInputObject<CancelShiftPaymentOrgBillingReqDTO> = {
    apiUrl: `${backendApiBasePathLocal}agency/org-billing/cancel-shift-payment-org-billing`,
    headers: { },
    object: req
  };

  const apiResponse: AxiosResponse<ServerResponse<string, Error>> =
  await remoteCallObject.remotePOSTCallV2<ServerResponse<string, Error>, CancelShiftPaymentOrgBillingReqDTO>(postCallInput);

  if (apiResponse && apiResponse.data && apiResponse.data.data) {
    return true;
  } else {
    return false;
  }
};

const CreateCorpJobberEmploymentDeductionAPICall = async (createReq: CreateCorpJobberEmploymentDeductionReqDTO): Promise<void | null> => {
  const postCallInput: RemotePOSTCallInputObject<CreateCorpJobberEmploymentDeductionReqDTO> = {
    apiUrl: `${backendApiBasePathLocal}workypay/agency/create-corp-jobber-employment-deduction`,
    headers: { },
    object: createReq
  };

  const apiResponse: AxiosResponse<ServerResponse<void, Error>> = 
  await remoteCallObject.remotePOSTCallV2<ServerResponse<void, Error>, CreateCorpJobberEmploymentDeductionReqDTO>(postCallInput);

  if (apiResponse && apiResponse.data) {
    return undefined;
  } else {
    return null;
  }
};

export const UpdateAgencyOrgBillStatusAPICall = async (updateReq: UpdateAgencyOrgBillStatusReqDTO): Promise<string | null> => {
  const remoteCallObject = RemoteExecutionServiceImpl();

  const postCallInput: RemotePOSTCallInputObject<UpdateAgencyOrgBillStatusReqDTO> = {
    apiUrl: `${backendApiBasePathLocal}agency/org-billing/update-agency-org-bill-status`,
    headers: {},
    object: updateReq
  };

  const apiResponse: AxiosResponse<ServerResponse<string, Error>> =
  await remoteCallObject.remotePOSTCallV2<ServerResponse<string, Error>, UpdateAgencyOrgBillStatusReqDTO>(postCallInput);

  if (apiResponse && apiResponse.data && apiResponse.data.data) {
    return apiResponse.data.data;
  } else {
    return null;
  }
};

/**
 * Maps jobbers to an organization location
 * @param mapJobbersToOrgLocationRequest - Request containing org name, org location name, and list of jobber emails
 * @returns AgencyObjClient or null if the request fails
 */
const MapJobbersToOrgLocationAPICall = async (
  mapJobbersToOrgLocationRequest: MapJobbersToOrgLocationRequestDTO
): Promise<AgencyObjClient | null> => {
  const postCallInput: RemotePOSTCallInputObject<MapJobbersToOrgLocationRequestDTO> = {
    apiUrl: `${backendApiBasePathLocal}agency/actions/map-jobbers-to-org-location`,
    headers: {},
    object: mapJobbersToOrgLocationRequest,
  };

  const apiResponse: AxiosResponse<ServerResponse<AgencyObjClient, Error>> =
    await remoteCallObject.remotePOSTCallV2<
      ServerResponse<AgencyObjClient, Error>,
      MapJobbersToOrgLocationRequestDTO
    >(postCallInput);

  if (apiResponse && apiResponse.data && apiResponse.data.data) {
    return apiResponse.data.data;
  } else {
    return null;
  }
};

const ResendJobberRegistrationEmailAPICall = async (
  request: ResendJobberRegistrationEmailRequestDTO
): Promise<AgencyObjClient | null> => {
  const postCallInput: RemotePOSTCallInputObject<ResendJobberRegistrationEmailRequestDTO> = {
    apiUrl: `${backendApiBasePathLocal}agency/jobber-actions/resend-registration-email`,
    headers: {},
    object: request,
  };

  const apiResponse: AxiosResponse<ServerResponse<AgencyObjClient, Error>> =
    await remoteCallObject.remotePOSTCallV2<
      ServerResponse<AgencyObjClient, Error>,
      ResendJobberRegistrationEmailRequestDTO
    >(postCallInput);

  if (apiResponse && apiResponse.data && apiResponse.data.data) {
    return apiResponse.data.data;
  } else {
    return null;
  }
};


/**
 * Updates the jobber matching type for an organization location
 * @param updateRequest - Request containing org name, org location name, and jobber matching type
 * @returns AgencyObjClient or null if the request fails
 */
const UpdateOrgLocationJobberMatchingTypeAPICall = async (
  updateRequest: UpdateOrgLocationJobberMatchingTypeRequestDTO
): Promise<AgencyObjClient | null> => {
  const postCallInput: RemotePOSTCallInputObject<UpdateOrgLocationJobberMatchingTypeRequestDTO> = {
    apiUrl: `${backendApiBasePathLocal}agency/actions/update-org-location-jobber-matching-type`,
    headers: {},
    object: updateRequest,
  };

  const apiResponse: AxiosResponse<ServerResponse<AgencyObjClient, Error>> =
    await remoteCallObject.remotePOSTCallV2<
      ServerResponse<AgencyObjClient, Error>,
      UpdateOrgLocationJobberMatchingTypeRequestDTO
    >(postCallInput);

  if (apiResponse && apiResponse.data && apiResponse.data.data) {
    return apiResponse.data.data;
  } else {
    return null;
  }
};

const CreateShiftTemplateAPICall = async (createTemplateReq: CreateShiftTemplateRequestDTO): Promise<AgencyObjClient | null> => {
    const postCallInput: RemotePOSTCallInputObject<CreateShiftTemplateRequestDTO> = {
        apiUrl: `${backendApiBasePathLocal}agency/actions/create-shift-template`,
        headers: {},
        object: createTemplateReq,
    };

    try {
        const apiResponse: AxiosResponse<ServerResponse<AgencyObjClient, Error>> =
            await remoteCallObject.remotePOSTCallV2<
                ServerResponse<AgencyObjClient, Error>,
                CreateShiftTemplateRequestDTO
            >(postCallInput);

        if (apiResponse && apiResponse.data && apiResponse.data.data) {
            return apiResponse.data.data;
        }
        return null;
    } catch (error) {
        console.error("Error creating shift template:", error);
        return null;
    }
};

/**
 * Adds hourly deduction config to an organization location
 * @param addRequest - Request containing org name, org location name, and deduction config
 * @returns AgencyObjClient or null if the request fails
 */
const AddOrgLocationHourlyDeductionConfigAPICall = async (
    addRequest: AddOrgLocationHourlyDeductionConfigRequestDTO
): Promise<AgencyObjClient | null> => {
    const postCallInput: RemotePOSTCallInputObject<AddOrgLocationHourlyDeductionConfigRequestDTO> = {
        apiUrl: `${backendApiBasePathLocal}agency/actions/add-org-location-hourly-deduction-config`,
        headers: {},
        object: addRequest,
    };

    try {
        const apiResponse: AxiosResponse<ServerResponse<AgencyObjClient, Error>> =
            await remoteCallObject.remotePOSTCallV2<
                ServerResponse<AgencyObjClient, Error>,
                AddOrgLocationHourlyDeductionConfigRequestDTO
            >(postCallInput);

        if (apiResponse && apiResponse.data && apiResponse.data.data) {
            return apiResponse.data.data;
        }
        return null;
    } catch (error) {
        console.error("Error adding org location hourly deduction config:", error);
        return null;
    }
};

/**
 * Deletes hourly deduction config detail from an organization location
 * @param deleteRequest - Request containing org name, org location name, and detail ID
 * @returns AgencyObjClient or null if the request fails
 */
const DeleteOrgLocationHourlyDeductionConfigAPICall = async (
    deleteRequest: DeleteOrgLocationHourlyDeductionConfigRequestDTO
): Promise<AgencyObjClient | null> => {
    const postCallInput: RemotePOSTCallInputObject<DeleteOrgLocationHourlyDeductionConfigRequestDTO> = {
        apiUrl: `${backendApiBasePathLocal}agency/actions/delete-org-location-hourly-deduction-config`,
        headers: {},
        object: deleteRequest,
    };

    try {
        const apiResponse: AxiosResponse<ServerResponse<AgencyObjClient, Error>> =
            await remoteCallObject.remotePOSTCallV2<
                ServerResponse<AgencyObjClient, Error>,
                DeleteOrgLocationHourlyDeductionConfigRequestDTO
            >(postCallInput);

        if (apiResponse && apiResponse.data && apiResponse.data.data) {
            return apiResponse.data.data;
        }
        return null;
    } catch (error) {
        console.error("Error deleting org location hourly deduction config:", error);
        return null;
    }
};

/**
 * Removes a worker type from an organization
 * @param removeRequest - Request containing org name and worker type unique identifier
 * @returns AgencyObjClient or null if the request fails
 */
const RemoveWorkerTypeFromOrgAPICall = async (
    removeRequest: RemoveWorkerTypeFromOrgRequestDTO
): Promise<AgencyObjClient | null> => {
    const postCallInput: RemotePOSTCallInputObject<RemoveWorkerTypeFromOrgRequestDTO> = {
        apiUrl: `${backendApiBasePathLocal}agency/actions/remove-worker-type-from-org`,
        headers: {},
        object: removeRequest,
    };

    try {
        const apiResponse: AxiosResponse<ServerResponse<AgencyObjClient, Error>> =
            await remoteCallObject.remotePOSTCallV2<
                ServerResponse<AgencyObjClient, Error>,
                RemoveWorkerTypeFromOrgRequestDTO
            >(postCallInput);

        if (apiResponse && apiResponse.data && apiResponse.data.data) {
            return apiResponse.data.data;
        }
        return null;
    } catch (error) {
        console.error("Error removing worker type from org:", error);
        return null;
    }
};

const CreateAgencyInvoiceDisplayConfigTemplateAPICall = async (
    request: CreateAgencyInvoiceDisplayConfigTemplateRequest
): Promise<AgencyInvoiceDisplayConfigTemplateResponse | null> => {
    const postCallInput: RemotePOSTCallInputObject<CreateAgencyInvoiceDisplayConfigTemplateRequest> = {
        apiUrl: `${backendApiBasePathLocal}v1/agency/billing-config/add-agency-invoice-display-config-template`,
        headers: {},
        object: request,
    };

    try {
        const apiResponse: AxiosResponse<ServerResponse<AgencyInvoiceDisplayConfigTemplateResponse, Error>> =
            await remoteCallObject.remotePOSTCallV2<
                ServerResponse<AgencyInvoiceDisplayConfigTemplateResponse, Error>,
                CreateAgencyInvoiceDisplayConfigTemplateRequest
            >(postCallInput);

        if (apiResponse && apiResponse.data && apiResponse.data.data) {
            return apiResponse.data.data;
        }
        return null;
    } catch (error) {
        console.error("Error adding agency invoice display config template:", error);
        return null;
    }
};

const UpdateAgencyOrgInvoiceConfigSpecAPICall = async (
    updateRequest: UpdateAgencyOrgInvoiceConfigSpecRequestDTO
): Promise<AgencyOrgInvoiceConfigSpecDTO | null> => {
    const postCallInput: RemotePOSTCallInputObject<UpdateAgencyOrgInvoiceConfigSpecRequestDTO> = {
        apiUrl: `${backendApiBasePathLocal}v1/agency/billing-config/update-org-invoice-config-spec`,
        headers: {},
        object: updateRequest,
    };

    try {
        const apiResponse: AxiosResponse<ServerResponse<AgencyOrgInvoiceConfigSpecDTO, Error>> =
            await remoteCallObject.remotePOSTCallV2<
                ServerResponse<AgencyOrgInvoiceConfigSpecDTO, Error>,
                UpdateAgencyOrgInvoiceConfigSpecRequestDTO
            >(postCallInput);

        if (apiResponse && apiResponse.data && apiResponse.data.data) {
            return apiResponse.data.data;
        }
        return null;
    } catch (error) {
        console.error("Error updating agency org invoice config spec:", error);
        return null;
    }
};

const UpdateOrgDisplayNameAPICall = async (
    request: UpdateOrgDisplayNameRequestDTO
): Promise<AgencyObjClient | null> => {
    const postCallInput: RemotePOSTCallInputObject<UpdateOrgDisplayNameRequestDTO> = {
        apiUrl: `${backendApiBasePathLocal}agency/actions/update-org-display-name`,
        headers: {},
        object: request,
    };

    try {
        const apiResponse: AxiosResponse<ServerResponse<AgencyObjClient, Error>> =
            await remoteCallObject.remotePOSTCallV2<
                ServerResponse<AgencyObjClient, Error>,
                UpdateOrgDisplayNameRequestDTO
            >(postCallInput);

        if (apiResponse && apiResponse.data && apiResponse.data.data) {
            return apiResponse.data.data;
        }
        return null;
    } catch (error) {
        console.error("Error updating org display name:", error);
        return null;
    }
};

export {
    AddJobberWorkerTypeToJobber,
    UpdateJobberWorkerTypePayRateAPICall,
    FullyHireJobberAPICall,
    CreateNewJobberWorkerTypePOSTAPICall,
    RegisterBulkJobbersPOSTAPICall,
    GetAgencyWTokenOnly,
    GetAllPaystubAPICall,
    GetOrgBillsAPICall,
    GetOrgBillHeadersPageAPICall,
    SearchOrgBillsByInvoiceNumberAPICall,
    GetOrgBillingCycleSummaryAPICall,
    GetActiveSATimeClock,
    PostRetrieveOrgAgencyBillPDF,
    PostDownloadOrgBillCsv,
    PostRetrieveJobberPayStubBillPDF,
    UpdateJobberNoteOnEmploymentConfigAPICall,
    UpdateJobberQuickBooksHoursSyncAPICall,
    GetAllJobberAvailbilities,
    DirectReAssignShiftAPIReq,
    CreateOrgOnAgency,
    AddFacilityToOrg,
    AddWorkerTypeToOrg,
    UpdateOrgWorkerTypePayRate,
    UpdateDefaultWorkerTypePayRate,
    UpdateJobberWorkerTypeLaborSalesTax,
    UpdateOrgWorkerTypeLaborSalesTax,
    UpdateOrgBillingWorkerTypeName,
    ChangePayCycleStatusAPICall,
    GetPayrollCycleByDateRangeAPICall,
    GetJobberPayStubForAgencyPayCycleAPICall,
    GetShiftPaymentByShiftIdAPICall,
    ModifyJobberWorkerHourlyRateAPICall,
    ModifyOrgBillingRateAPICall,    
    CancelShiftPaymentWorkerPayAPICall,
    CancelShiftPaymentOrgBillingAPICall,
    CreateCorpJobberEmploymentDeductionAPICall,
    CreateCorpToOrgClientBillingConfigAPICall,
    CreateAgencyInvoiceDisplayConfigTemplateAPICall,
    UploadBillingConfigIconAPICall,
    MapJobbersToOrgLocationAPICall,
    UpdateOrgLocationJobberMatchingTypeAPICall,
    ChangeJobberStatusAPICall,
    ResendJobberRegistrationEmailAPICall,
    CreateShiftAssignmentConfigRuleAPICall,
    CreateShiftAssignmentConfigRuleDetail,
    AssignJobberToShiftAPICall,
    CreateShiftTemplateAPICall,
    AddOrgLocationHourlyDeductionConfigAPICall,
    DeleteOrgLocationHourlyDeductionConfigAPICall,
    RemoveWorkerTypeFromOrgAPICall,
    UpdateAgencyOrgInvoiceConfigSpecAPICall,
    UpdateOrgDisplayNameAPICall
};